import React, {useEffect} from 'react'
import {
    Flex,
    VStack,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import SanitizedHtml from '../../../components/html-sanitizer'
import {Helmet} from 'react-helmet'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// HOOKS
import {useParams, useLocation} from 'react-router-dom'
import usePHPBKApi from '../../../custom-api/hooks/use-phpkb-api'
import {useArticleMeta} from '../../../hooks/use-category-meta'
//UTILS
import routingObject from '../../../utils/static-routes'
import {remCalc} from '../../../theme/_utils'
import {decodeHtmlEntities} from '../../../utils/utils'

export default function ArticlePage({
    setGoBackText,
    setGoBackToPath,
    setPageTitle,
    listOfCategories,
    setIsLoading,
}) {
    const {usePHPKBGetArticle} = usePHPBKApi()
    const {article_id} = useParams() || ''
    const styles = useMultiStyleConfig('Faq', {}) || {}
    const {singleArticleStyles} = styles
    const {customParams} = getConfig()
    const location = useLocation()
    const {data, isLoading} = usePHPKBGetArticle(
        {
            article_id,
        },
        {
            enabled: Boolean(article_id),
        },
    )
    useEffect(() => {
        setIsLoading(isLoading)
    }, [isLoading])

    const article = data?.hits?.hits[0]?._source || null

    const formatCategoryName = (categoryName) => {
        return decodeHtmlEntities(categoryName).trim().replace(/\s+/g, '-').replace(/[?]/g, '')
    }

    const buildUlr = (categories, articleCategoryId) => {
        const allCategories = Object.values(categories || {})

        const topLevelMatch = allCategories.find(
            (category) => category?.category_id === articleCategoryId,
        )

        if (topLevelMatch)
            return `${routingObject.FAQ_PATH}/${formatCategoryName(
                topLevelMatch?.category_name,
            )}/t/${article?.category_id}`

        for (const category of allCategories) {
            const topics = Object.values(category?.topics || {})
            const topicMatch = topics.find((topic) => topic?.category_id === articleCategoryId)
            if (topicMatch)
                return `${routingObject.FAQ_PATH}/${formatCategoryName(
                    category?.category_name,
                )}/${formatCategoryName(topicMatch?.category_name)}/t/${topicMatch?.category_id}`
        }

        return null
    }

    useEffect(() => {
        if (article !== null) {
            const path = buildUlr(listOfCategories?.data, article?.category_id?.toString())
            setGoBackText(article?.category_name)
            setGoBackToPath(path)
            setPageTitle(article?.article_title)
        }
    }, [article])

    const {metaTitle, metaDescription} = useArticleMeta(article)

    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                item: {
                    '@id': 'https://' + customParams.currentHost + '/',
                    name: 'Home',
                },
            },
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': currentPath,
                    name: metaTitle || '',
                },
            },
        ],
    }

    const articleQuestion = article?.article_title
        ? decodeHtmlEntities(article.article_title)
        : null
    const articleAnswer = article?.article_content
        ? article.article_content
              .replace(/<[^>]+>/g, '')
              .replace(/\s+/g, ' ')
              .trim()
        : null

    const faqSchemaMarkup =
        articleQuestion && articleAnswer
            ? {
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: [
                      {
                          '@type': 'Question',
                          name: articleQuestion,
                          acceptedAnswer: {
                              '@type': 'Answer',
                              text: articleAnswer,
                          },
                      },
                  ],
              }
            : null

    return (
        <>
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaDescription && <meta name="description" content={metaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                {faqSchemaMarkup && (
                    <script type="application/ld+json">
                        {JSON.stringify(faqSchemaMarkup, null, 2)}
                    </script>
                )}
            </Helmet>

            <VStack w="100%" h="auto" alignItems="flex-start">
                {isLoading ? (
                    <></>
                ) : article ? (
                    <SanitizedHtml
                        as={Flex}
                        html={article.article_content}
                        width={'100%'}
                        flexDirection="column"
                        gap={remCalc(10)}
                        sx={{
                            p: singleArticleStyles?.paragraphs,
                            li: singleArticleStyles?.listItems,
                            em: singleArticleStyles?.strongText,
                            strong: singleArticleStyles?.strongText,
                            iframe: {
                                width: '100% !important',
                                maxWidth: '100% !important',
                                height: 'auto',
                                aspectRatio: '16/9',
                            },
                        }}
                    />
                ) : (
                    <></>
                )}
            </VStack>
        </>
    )
}

ArticlePage.propTypes = {
    setGoBackText: PropTypes.func,
    setGoBackToPath: PropTypes.func,
    setPageTitle: PropTypes.func,
    listOfCategories: PropTypes.object,
    setIsLoading: PropTypes.func,
}
